import { useEffect, useRef } from 'react'

/**
 * Calls `onReturn` with how long the page was away whenever the participant
 * comes back to it: the tab or the browser shown again after another app,
 * a page restored from the back-forward cache, or the network back. A phone
 * stops timers while it is away, so this - not a timer - is when a page can
 * find out what it missed.
 */
export const usePageReturn = (onReturn: (awayMs: number) => void): void => {
	const latest = useRef(onReturn)
	latest.current = onReturn

	useEffect(() => {
		let hiddenAt: number | undefined =
			document.visibilityState === 'hidden' ? Date.now() : undefined

		const onVisibility = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAt = Date.now()
			} else if (hiddenAt !== undefined) {
				const awayMs = Date.now() - hiddenAt
				hiddenAt = undefined
				latest.current(awayMs)
			}
		}
		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) latest.current(Number.POSITIVE_INFINITY)
		}
		const onOnline = () => latest.current(0)

		document.addEventListener('visibilitychange', onVisibility)
		window.addEventListener('pageshow', onPageShow)
		window.addEventListener('online', onOnline)
		return () => {
			document.removeEventListener('visibilitychange', onVisibility)
			window.removeEventListener('pageshow', onPageShow)
			window.removeEventListener('online', onOnline)
		}
	}, [])
}
